import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Table,
  Button,
  Spinner,
  Form,
  InputGroup,
  Pagination,
  Badge
} from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export default function ManageCustomers() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem("selectedCompany");
    return storedCompany ? JSON.parse(storedCompany).id : "";
  });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // 🔹 Load companies on mount and validate selectedCompany
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const companyData = res.data || [];
        setCompanies(companyData);

        // Validate stored selectedCompany
        const storedCompany = localStorage.getItem("selectedCompany");
        if (storedCompany) {
          const parsedCompany = JSON.parse(storedCompany);
          const isValidCompany = companyData.some((c) => c.id === parsedCompany.id);
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompany");
            setSelectedCompany("");
          }
        }
      } catch (err) {
        console.error("fetchCompanies", err);
        Swal.fire("Error", "Failed to load companies", "error");
        setSelectedCompany("");
      }
    }
    fetchCompanies();
  }, []);

  // 🔹 Fetch customers when selectedCompany or search changes
  useEffect(() => {
    if (selectedCompany) {
      fetchCustomers(1);
    } else {
      setCustomers([]);
      setTotalPages(1);
    }
  }, [selectedCompany, search]);

  // 🔹 Fetch customers
  const fetchCustomers = async (pageNo = 1) => {
    if (!selectedCompany) {
      setCustomers([]);
      setTotalPages(1);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: pageNo,
        pageSize,
        q: search || undefined,
      };
      const res = await axios.get(
        `${API_BASE_URL}/companies/${selectedCompany}/customers`,
        { params }
      );
      setCustomers(res.data.customers || []);
      setTotalPages(res.data.totalPages || 1);
      setPage(pageNo);
    } catch (err) {
      console.error("fetchCustomers", err);
      Swal.fire("Error", "Failed to load customers", "error");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Phone / Email action handlers with themed confirmation modals (consistent with other pages)
  const handlePhoneAction = (phone) => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");
    Swal.fire({
      title: "Call Customer?",
      html: `Do you want to call <strong>${phone}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Call Now",
      confirmButtonColor: "#0d6efd",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = `tel:${cleanPhone}`;
      }
    });
  };

  const handleEmailAction = (email) => {
    Swal.fire({
      title: "Send Email?",
      html: `Compose email to <strong>${email}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Open Email Client",
      confirmButtonColor: "#0d6efd",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = `mailto:${email}`;
      }
    });
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-4">Manage Customers</h4>

      {/* 🔹 Company Selector */}
      <Row className="mb-3">
        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Company</Form.Label>
            <Form.Select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                localStorage.setItem(
                  "selectedCompany",
                  JSON.stringify(companies.find((c) => c.id === parseInt(e.target.value)) || {})
                );
              }}
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* 🔹 Search */}
      <Row className="mb-3">
        <Col xs={12} md={6}>
          <InputGroup>
            <Form.Control
              placeholder="Search customers (name / email / phone)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="outline-primary"
              className="rounded-start-0"
              onClick={() => fetchCustomers(1)}
            >
              <i className="bi bi-search"></i>
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {/* 🔹 Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th className="text-end">Total Bookings</th>
                  <th className="text-end">Total Spent (₹)</th>
                  <th>Latest Booking</th>
                  {/* <th className="text-center">Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-3">
                      {selectedCompany ? "No customers found" : "Please select a company"}
                    </td>
                  </tr>
                ) : (
                  customers.map((customer, i) => (
                    <tr key={customer.id}>
                      <td>{(page - 1) * pageSize + i + 1}</td>
                      <td>
                        <strong>{customer.name}</strong>
                        {customer.gender && (
                          <span className="badge bg-secondary ms-2">
                            {customer.gender}
                          </span>
                        )}
                      </td>
                      <td>
                        {customer.email ? (
                          <a
                            href="#"
                            className="text-primary text-decoration-none"
                            onClick={(e) => {
                              e.preventDefault();
                              handleEmailAction(customer.email);
                            }}
                            title="Click to email"
                          >
                            {customer.email}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {customer.phone ? (
                          <a
                            href="#"
                            className="text-primary text-decoration-none"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePhoneAction(customer.phone);
                            }}
                            title="Click to call"
                          >
                            {customer.phone}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="text-end">
                        <Badge bg="info">{customer.total_bookings}</Badge>
                      </td>
                      <td className="text-end">₹{customer.total_spent.toFixed(2)}</td>
                      <td>
                        {customer.latest_booking_date
                          ? new Date(customer.latest_booking_date).toLocaleDateString()
                          : "-"}
                      </td>
                      {/* <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          href={`/customer/${customer.id}`}
                          target="_blank"
                          title="View Customer Profile"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              {Array.from({ length: totalPages }).map((_, idx) => (
                <Pagination.Item
                  key={idx}
                  active={page === idx + 1}
                  onClick={() => fetchCustomers(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
}